



































































































import Vue from 'vue';
import Component from 'vue-class-component';
import urlJoin from 'url-join';
import { DEFAULT_DEFERRED_ORDER_API_URL, isOrder, type Order } from '@/composables/deferred';
import { Location, NavigationGuardNext, Route } from 'vue-router';
import {
    OrderResultInvalidWithCart,
    OrderResultInvalidWithoutCart,
    pollingState,
    StateOrderReceived,
    StateOrderResultInvalid,
    StatePollingFailed,
    StatePollingTimeout,
    States,
    timeoutState
} from '@/composables/deferred/deferred.state';
import {
    createDefaultPollConfig,
    MAX_POLL_LIMIT,
    MIN_POLL_INTERVAL_MS,
    PollConfig
} from '@/composables/deferred/deferred.config';
import { errorToSlugData, validationError } from '@/composables/deferred/deferred.errors';

const HAPPY_DELAY_REDIRECT = 750 as const;
const SAD_DELAY_REDIRECT = 3_000 as const;

@Component
export default class DeferredPlaceOrder extends Vue {

    config: PollConfig = createDefaultPollConfig();
    state: States = pollingState;
    pollIndex = 0;
    pollTimeout = 0;

    loopingEllipsis = '\u00a0\u00a0\u00a0';
    loopingEllipsisInterval = 0;

    token: string | null = null;
    shopId: string | null = null;
    fromRoute: Location | null = null;

    mounted(): void {
        this.parseSettings(this.$route.query);

        this.token = this.$route.params.deferred_token;
        this.shopId = this.$route.params.shop_id;

        if (!this.validateParams()) {
            return;
        }

        const timeoutCallback = async (): Promise<void> => {
            if (await this.poll()) {
                this.pollTimeout = window.setTimeout(
                    timeoutCallback,
                    this.config.intervalMs
                );
            }
        };

        if (this.pollIndex < this.config.limit) {
            this.pollTimeout = window.setTimeout(
                timeoutCallback,
                this.config.intervalMs
            );
        }

        this.loopingEllipsisInterval = window.setInterval(() => {
            this.setLoopingEllipsis();
        }, 250);
    }

    beforeDestroy(): void {
        window.clearTimeout(this.pollTimeout);
        window.clearInterval(this.loopingEllipsisInterval);
    }

    validateParams(): boolean {
        if (!this.token || !this.shopId) {
            this.state = {
                state: 'invalid',
                fetchError: 'invalid',
                fetchErrorSlug: errorToSlugData('invalid'),
                showFetchStatusButton: false,
                orderResult: {
                    valid: false,
                    error: this.$t(validationError(!this.token ? 'token' : 'shopid').key) as string,
                    delayMs: 3_000,
                    toShop: this.stillHasCart() ? 'back' : 'reset',
                    redirectUrl: null,
                },
            } satisfies StateOrderResultInvalid;

            return false;
        }

        return true;
    }

    async poll(): Promise<boolean> {
        window.clearTimeout(this.pollTimeout);
        this.pollIndex++;

        const mayContinue = this.pollIndex < this.config.limit;

        if (!mayContinue) {
            if (this.state.state === 'polling') {
                this.state = timeoutState satisfies StatePollingTimeout;
            }

            return false;
        }

        await this.fetchDeferredOrder();

        return mayContinue;
    }

    async fetchDeferredOrderManual(): Promise<void> {
        await this.fetchDeferredOrder();
    }

    async fetchDeferredOrder(): Promise<void> {
        const response: Response | null = await this.fetchDeferredStatus();

        if (!response) {
            return;
        }

        // Continue to response / check body
        if (await this.nautilusIsStillPending(response)) {
            return;
        }

        const order: Order | null = await this.transformDeferredOrder(response);

        if (!order) {
            return;
        }

        this.state = this.resultValid(order) satisfies StateOrderReceived;
    }

    async fetchDeferredStatus(): Promise<Response | null> {
        if (!this.token || !this.shopId) {
            return null;
        }

        const url = urlJoin(
            process.env.VUE_APP_DEFERRED_ORDER_API_URL ||
            DEFAULT_DEFERRED_ORDER_API_URL,
            this.shopId,
            'status'
        );

        try {
            const response = await fetch(url, {
                headers: {
                    'x-botprot-verification': this.token,
                },
                method: 'GET',
            });

            this.parseSettings({
                pollintervalms: response.headers.get('X-POLL-INTERVAL-MS'),
                polllimit: response.headers.get('X-POLL-LIMIT'),
            });

            if (response.status >= 400) {
                this.state = await this.resultInvalid(response) satisfies StateOrderResultInvalid;
                return null;
            }

            return response;
        } catch (e) {
            this.state = this.resultFailed(e) satisfies StatePollingFailed;

            return null;
        }
    }

    async transformDeferredOrder(response: Response): Promise<Order | null> {
        try {
            const order = await response.json();

            this.parseSettings(order);

            if (!isOrder(order)) {
                // This is an unexpected response, but we can't do anything about it.
                // There is an argument that we should stop polling, but that would
                // soft-lock the visitor.

                this.state = this.resultFailed(null, 'unexpected') satisfies StatePollingFailed;

                return null;
            }

            return order;
        } catch (e) {
            this.state = this.resultFailed(e, 'unexpected') satisfies StatePollingFailed;
        }

        return null;
    }

    async resultInvalid(response: Response): Promise<StateOrderResultInvalid> {
        let error: string;

        try {
            error = await response.text();
        } catch (e) {
            error = 'Failed to parse error';
        }

        if (this.stillHasCart()) {
            window.setTimeout(() => {
                void this.$router.push(this.fromRoute);
            }, SAD_DELAY_REDIRECT);

            return {
                state: 'invalid',
                fetchError: 'invalid',
                fetchErrorSlug: errorToSlugData('invalid'),
                showFetchStatusButton: false,
                orderResult: {
                    valid: false,
                    error,
                    delayMs: SAD_DELAY_REDIRECT,
                    toShop: 'back',
                    redirectUrl: null,
                } satisfies OrderResultInvalidWithCart,
            } satisfies StateOrderResultInvalid;
        }

        return {
            state: 'invalid',
            fetchError: 'invalid',
            fetchErrorSlug: errorToSlugData('invalid'),
            showFetchStatusButton: false,
            orderResult: {
                valid: false,
                error,
                delayMs: SAD_DELAY_REDIRECT,
                toShop: 'reset',
                redirectUrl: null,
            } satisfies OrderResultInvalidWithoutCart,
        } satisfies StateOrderResultInvalid;
    }

    resultFailed(error: unknown, fetchError: StatePollingFailed['fetchError'] = 'failed'): StatePollingFailed {
        console.error('Failed to poll deferred order', { error });

        return {
            state: 'failed',
            fetchError,
            fetchErrorSlug: errorToSlugData(fetchError),
            showFetchStatusButton: false,
            orderResult: null,
        };
    }

    resultValid(order: Order): StateOrderReceived {
        console.info(
            'Fetched deferred order',
            order.order_id,
            order
        );

        window.setTimeout(() => {
            // If the shop is loaded inside an iframe, overwrite the location of its parent
            if (window.top && window.top !== window.self) {
                window.top.location = order.redirectUrl;
            } else {
                window.location.href = order.redirectUrl;
            }
        }, HAPPY_DELAY_REDIRECT);

        return {
            state: 'ok',
            fetchError: null,
            showFetchStatusButton: false,
            orderResult: {
                valid: true,
                error: null,
                delayMs: HAPPY_DELAY_REDIRECT,
                toShop: null,
                redirectUrl: order.redirectUrl,
            },
        }
    }

    parseSettings(data: unknown): 'stop' | void {
        if (!data || typeof data !== 'object') {
            return;
        }

        if (!data || typeof data !== 'object') {
            return;
        }

        if (
            'pollintervalms' in data &&
            (typeof data.pollintervalms === 'string' ||
                typeof data.pollintervalms === 'number')
        ) {
            this.config.intervalMs = Math.max(
                MIN_POLL_INTERVAL_MS,
                typeof data.pollintervalms === 'number'
                    ? data.pollintervalms
                    : parseInt(data.pollintervalms, 10)
            );
        }

        if (
            'polllimit' in data &&
            (typeof data.polllimit === 'string' ||
                typeof data.polllimit === 'number')
        ) {
            this.config.limit = Math.min(
                MAX_POLL_LIMIT,
                typeof data.polllimit === 'number'
                    ? data.polllimit
                    : parseInt(data.polllimit, 10)
            );

            if (this.pollIndex >= this.config.limit) {
                window.clearTimeout(this.pollTimeout);

                return 'stop';
            }
        }
    }

    async nautilusIsStillPending(response?: Response): Promise<boolean> {
        if (response?.ok && response?.status === 204) {
            try {
                this.parseSettings(await response.json());
            } catch (e) {
                /* Silent, very likely this WILL fail... That's OK */
            }

            if (this.state.state === 'failed') {
                this.state = this.pollIndex >= this.config.limit ? timeoutState : pollingState;
            }

            return true;
        }

        return false;
    }

    resetAndRestartShop(): void {
        const shopId = this.shopId;

        if (!shopId) {
            return;
        }

        const shopRoute = this.$router.resolve({
            name: 'shop',
            params: { shop_id: shopId },
        });

        window.location.href = shopRoute.href;
    }

    stillHasCart(this: DeferredPlaceOrder): this is { fromRoute: Location; } {
        if (this.$route.name !== 'deferred-place-order' || !this.fromRoute) {
            return false;
        }

        try {
            return this.$shop && this.$shop.cart && this.$shop.cart.hasItems();
        } catch (e) {
            /* No-op */
        }

        return false;
    }

    setLoopingEllipsis(): void {
        if (this.state.state !== 'polling') {
            this.loopingEllipsis = ''.padEnd(3, '\u00a0');
            return;
        }

        this.loopingEllipsis = '.'
            .repeat(((Date.now() / 1000) % 3) + 1)
            .padEnd(3, '\u00a0');
    }

    beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext<DeferredPlaceOrder>): void {
        next((vm) => {
            if (from.name) {
                vm.fromRoute = {
                    name: from.name,
                    params: { ...from.params },
                    query: { ...from.query },
                };
            }
        });
    }
}
